import React, { useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import PartCreateFilePicker from './PartCreateFilePicker';
import FootprintBBoxApproval from './FootprintBBoxApproval';
import PartCreateReview from './PartCreateReview';
import type { SelectedFile } from './PartCreateFilePicker';
import {
  detectFootprint,
  detectFootprintUpload,
  extractCroppedFootprint,
  extractFromDriveStream,
  extractFromUpload,
  type DetectedPage,
  type DatasheetFrontmatter,
  type ExtractResponse,
  type ExtractProgressEvent,
} from '@/services/datasheetService';

type PipelineStep = 'file-picker' | 'detecting' | 'footprint-bbox' | 'extracting' | 'review';

interface PartCreatePipelineProps {
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * Orchestrates the Part Create flow pipeline:
 * Step 1: File picker (Google Drive or device upload)
 * Step 2: Detecting footprint + starting text extraction in background
 * Step 3: Footprint bbox approval (with early-extracted fields shown)
 * Step 4: Extracting footprint (text extraction may already be done)
 * Step 5: Review extracted data and save to part code sheet
 */
const PartCreatePipeline: React.FC<PartCreatePipelineProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<PipelineStep>('file-picker');
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Intercept close — show confirmation if past file-picker step
  const handleCloseAttempt = () => {
    if (step === 'file-picker') {
      onCancel(); // No work done yet, safe to close immediately
    } else {
      setShowConfirmClose(true);
    }
  };

  // Listen for Escape key
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (showConfirmClose) {
          setShowConfirmClose(false); // Close the confirm dialog itself
        } else {
          handleCloseAttempt();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, showConfirmClose]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extraction progress (SSE)
  const [extractProgress, setExtractProgress] = useState<ExtractProgressEvent | null>(null);

  // Footprint detection
  const [detectedPages, setDetectedPages] = useState<DetectedPage[]>([]);
  const detectAbortRef = useRef(false);

  // Extraction results
  const [frontmatter, setFrontmatter] = useState<DatasheetFrontmatter | null>(null);
  const [footprint, setFootprint] = useState<Record<string, unknown> | null>(null);
  const [footprintPng, setFootprintPng] = useState<string | null>(null); // cropped_png_base64
  const [sourceFilename, setSourceFilename] = useState('');
  const [userHint, setUserHint] = useState<string | undefined>();
  const [selectedTypeCode, setSelectedTypeCode] = useState<string | undefined>();
  const [nextPartNumber, setNextPartNumber] = useState<string | undefined>();

  // Early text extraction — started in background during detection so frontmatter
  // is available by the time the user reaches the bbox approval step.
  const earlyTextPromiseRef = useRef<Promise<ExtractResponse | null> | null>(null);

  // Convert local file to base64 (strips data URI prefix)
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]); // strip "data:...;base64,"
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Run footprint detection for a given file
  const runDetection = async (file: SelectedFile) => {
    setStep('detecting');
    setDetectError(null);
    detectAbortRef.current = false;

    try {
      let result;
      if (file.source === 'drive' && file.driveFile) {
        result = await detectFootprint(file.driveFile.id);
      } else if (file.source === 'device' && file.localFile) {
        const b64 = await fileToBase64(file.localFile);
        result = await detectFootprintUpload(b64, file.localFile.type, file.localFile.name);
      } else {
        // No valid file — skip to extraction without footprint
        await runExtraction(null);
        return;
      }

      // User may have cancelled or gone back while detecting
      if (detectAbortRef.current) return;

      setSourceFilename(result.source_filename || file.name);
      const pages = result.pages;
      setDetectedPages(pages);

      if (pages.length > 0) {
        setStep('footprint-bbox');
      } else {
        // No dimensional drawing found — skip to extraction
        await runExtraction(null);
      }
    } catch (err) {
      if (detectAbortRef.current) return;
      setDetectError(err instanceof Error ? err.message : 'Footprint detection failed');
    }
  };

  // Start text extraction in the background (runs parallel with detection)
  const startEarlyTextExtraction = (file: SelectedFile) => {
    const promise = (async (): Promise<ExtractResponse | null> => {
      try {
        if (file.source === 'drive' && file.driveFile) {
          return await extractFromDriveStream(
            file.driveFile.id,
            (event) => setExtractProgress(event),
          );
        } else if (file.source === 'device' && file.localFile) {
          const b64 = await fileToBase64(file.localFile);
          return await extractFromUpload(b64, file.localFile.type, file.localFile.name);
        }
        return null;
      } catch (err) {
        console.error('[PartCreate] Early text extraction failed:', err);
        return null;
      }
    })();
    earlyTextPromiseRef.current = promise;

    // Update frontmatter as soon as it arrives (user may be on bbox step)
    promise.then(result => {
      if (result) {
        setFrontmatter(result.frontmatter ?? null);
        setSourceFilename(prev => result.source_filename || prev);
      }
    });
  };

  // Step 1: File selected → run footprint detection + start text extraction
  const handleFileSelected = async (file: SelectedFile) => {
    setSelectedFile(file);
    startEarlyTextExtraction(file);
    await runDetection(file);
  };

  // Step 3: BBox approved → run extraction
  const handleBBoxApprove = async (
    pageIndex: number,
    bbox: { x1: number; y1: number; x2: number; y2: number },
    hint?: string,
    typeCode?: string,
    partNum?: string,
  ) => {
    setUserHint(hint);
    setSelectedTypeCode(typeCode);
    setNextPartNumber(partNum);
    await runExtraction({ pageIndex, bbox, userHint: hint });
  };

  // Step 3 skip: no footprint needed
  const handleBBoxSkip = async () => {
    await runExtraction(null);
  };

  // Step 4: Run extraction (footprint + await early text extraction)
  const runExtraction = async (
    footprintParams: { pageIndex: number; bbox: { x1: number; y1: number; x2: number; y2: number }; userHint?: string } | null,
  ) => {
    setStep('extracting');
    setExtractError(null);

    try {
      const promises: Promise<unknown>[] = [];

      // Reuse the early text extraction that was started during detection.
      // If it hasn't been started (shouldn't happen), start it now as fallback.
      const textPromise = earlyTextPromiseRef.current ?? (async (): Promise<ExtractResponse | null> => {
        try {
          if (selectedFile?.source === 'drive' && selectedFile.driveFile) {
            return await extractFromDriveStream(
              selectedFile.driveFile.id,
              (event) => setExtractProgress(event),
            );
          } else if (selectedFile?.source === 'device' && selectedFile.localFile) {
            const b64 = await fileToBase64(selectedFile.localFile);
            return await extractFromUpload(b64, selectedFile.localFile.type, selectedFile.localFile.name);
          }
          return null;
        } catch (err) {
          console.error('[PartCreate] Text extraction failed:', err);
          return null;
        }
      })();
      promises.push(textPromise);

      // Footprint extraction (if bbox approved)
      const footprintPromise = (async () => {
        if (!footprintParams || !detectedPages[footprintParams.pageIndex]) return null;
        try {
          const page = detectedPages[footprintParams.pageIndex];
          const result = await extractCroppedFootprint(
            page.page_png_base64,
            footprintParams.bbox,
            undefined,
            sourceFilename || selectedFile?.name,
            footprintParams.userHint,
          );
          if (result.cropped_png_base64) {
            setFootprintPng(result.cropped_png_base64);
          }
          return result.footprint;
        } catch (err) {
          console.error('[PartCreate] Footprint extraction failed:', err);
          return null;
        }
      })();
      promises.push(footprintPromise);

      const [textResult, fpResult] = await Promise.all(promises);

      const extractResult = textResult as ExtractResponse | null;
      if (extractResult?.frontmatter) setFrontmatter(extractResult.frontmatter);
      setFootprint(fpResult as Record<string, unknown> | null);
      setSourceFilename(extractResult?.source_filename || sourceFilename || selectedFile?.name || '');
      setStep('review');
    } catch (err) {
      setExtractError(err instanceof Error ? err.message : 'Extraction failed');
    }
  };

  // Navigate back to file picker, aborting any in-progress detection
  const handleBackToFilePicker = () => {
    detectAbortRef.current = true;
    earlyTextPromiseRef.current = null;
    setDetectedPages([]);
    setDetectError(null);
    setFrontmatter(null);
    setStep('file-picker');
  };

  // Render current step + confirmation dialog
  const renderStep = () => {
    switch (step) {
      case 'file-picker':
        return <PartCreateFilePicker onSelect={handleFileSelected} onCancel={handleCloseAttempt} />;

      case 'detecting':
        return (
          <LoadingOverlay
            message={detectError || 'Detecting dimensional drawing...'}
            error={!!detectError}
            onCancel={handleCloseAttempt}
            onBack={handleBackToFilePicker}
            onRetry={detectError && selectedFile ? () => runDetection(selectedFile) : undefined}
          />
        );

      case 'footprint-bbox':
        return detectedPages.length > 0 ? (
          <FootprintBBoxApproval
            detectedPages={detectedPages}
            sourceFilename={sourceFilename || selectedFile?.name || ''}
            frontmatter={frontmatter}
            onApprove={handleBBoxApprove}
            onSkip={handleBBoxSkip}
            onBack={handleBackToFilePicker}
            onCancel={handleCloseAttempt}
          />
        ) : null;

      case 'extracting':
        return (
          <LoadingOverlay
            message={extractError || extractProgress?.message || 'Extracting datasheet information...'}
            progress={extractProgress?.progress}
            error={!!extractError}
            onCancel={handleCloseAttempt}
            onRetry={extractError ? handleBackToFilePicker : undefined}
          />
        );

      case 'review':
        return (
          <PartCreateReview
            frontmatter={frontmatter}
            footprint={footprint}
            footprintPng={footprintPng}
            sourceFilename={sourceFilename || selectedFile?.name || ''}
            sourceFileId={selectedFile?.driveFile?.id}
            userHint={userHint}
            initialTypeCode={selectedTypeCode}
            initialPartNumber={nextPartNumber}
            onComplete={onComplete}
            onBack={() => detectedPages.length > 0 ? setStep('footprint-bbox') : handleBackToFilePicker()}
            onCancel={handleCloseAttempt}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderStep()}
      {showConfirmClose && (
        <ConfirmCloseDialog
          onConfirm={onCancel}
          onDismiss={() => setShowConfirmClose(false)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Loading overlay
// ---------------------------------------------------------------------------

const LoadingOverlay: React.FC<{
  message: string;
  progress?: number;
  error?: boolean;
  onCancel: () => void;
  onBack?: () => void;
  onRetry?: () => void;
}> = ({ message, progress, error, onCancel, onBack, onRetry }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl px-8 py-10 flex flex-col items-center gap-4 max-w-6xl w-full m-4">
      {!error && <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />}
      <p className={`text-sm text-center ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>
        {message}
      </p>
      {typeof progress === 'number' && !error && (
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
      <div className="flex gap-2 mt-2">
        {onBack && (
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Back
          </button>
        )}
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        )}
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Confirm close dialog
// ---------------------------------------------------------------------------

const ConfirmCloseDialog: React.FC<{
  onConfirm: () => void;
  onDismiss: () => void;
}> = ({ onConfirm, onDismiss }) => (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl px-6 py-5 max-w-sm m-4 flex flex-col items-center gap-3">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center">
        Are you sure you want to close?
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        Your extraction progress will be lost.
      </p>
      <div className="flex gap-3 mt-1">
        <button
          onClick={onDismiss}
          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600"
        >
          Continue Working
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

export default PartCreatePipeline;
